import React, { useState, useMemo, useEffect, useContext, useRef } from 'react';
import * as GiIcons from 'react-icons/gi';
import * as FaIcons from 'react-icons/fa';
import { IoMdAlert } from 'react-icons/io';
import { BiShow, BiCog } from 'react-icons/bi';
import { TfiExport } from 'react-icons/tfi';
import PageContainer from '../../../components/layout/PageContainer';
import AuthContext from '../../../contexts/auth';
import BtnSync from '../../../components/BtnSync';
import PageHeader from '../../../components/layout/PageHeader';
import ContentCard from '../../../components/layout/ContentCard';
import ExpandableTable from '../../../components/data/ExpandableTable';
import Pagination from '../../../components/data/Pagination';
import SearchFilter from '../../../components/data/SearchFilter';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import NumberWithPercentage from '../../../components/NumberWithPercentage';
import Spinner from '../../../components/Spinner';
import { exportToExcelGeneral, translate, verifyHideCountryItems } from '../../../utils/Common';
import usePlatformsGeneral from './usePlatformsGeneral';
import PlatformDetails from './PlatformDetails';
import DuplicatePlatformModal from './CreatePlatform/DuplicatePlatformModal';
import PlatformQuickInfo from './components/PlatformQuickInfo';
import styles from './PlatformsGeneral.module.css';
import { useLocation, useHistory } from 'react-router-dom';
import { verifyAccessViewPermissions } from '../../../config/permissions';

const General = () => {
  const userContext = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const [platformSelected, setPlatformSelected] = useState(null);

  const [canViewModalCreate, setCanViewModalCreate] = useState(false);
  const [localSelectedField, setLocalSelectedField] = useState('locationName');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const canRead = verifyAccessViewPermissions(['read'], { path: location.pathname });
  const canSearch = verifyAccessViewPermissions(['search'], { path: location.pathname });
  const canCreate = verifyAccessViewPermissions(['create'], { path: location.pathname });
  const canExportAction = verifyAccessViewPermissions(['export'], { path: location.pathname });
  const canSyncAll = verifyAccessViewPermissions(['sync-all'], { path: location.pathname });
  const canViewDetails =
    verifyAccessViewPermissions(['view-details'], { path: location.pathname });
  const canEditConfiguration = verifyAccessViewPermissions(['edit-configuration'], { path: location.pathname });

  const {
    platforms,
    filteredPlatforms,
    platformArray,
    stats,
    loading,
    error,
    pagination,
    filters,
    selectedField,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    onRefresh,
  } = usePlatformsGeneral();

  // Opened from another screen (the home locker summary) with ?locker=<id>.
  // The detail wants the row object, so we wait for this screen's own data and
  // pick the match from it rather than inventing a partial one.
  // True while a ?locker= link is still being resolved. Without it the screen
  // paints the full list first and swaps to the detail a beat later, which reads
  // as a jump. It holds the query string already resolved, so going back from the
  // detail does not immediately re-open it from the same still-present query
  // string (the list refresh alone changes `platformArray` and re-runs the
  // effect, which is what made "Back" look like it did nothing).
  const deepLinkResolved = useRef(null);
  const deepLinkId = new URLSearchParams(location.search).get('locker');
  const resolvingDeepLink =
    Boolean(deepLinkId) && deepLinkResolved.current !== location.search && !platformSelected;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('locker');
    const name = params.get('lockerName');
    // `platformArray` is the unpaginated list; `platforms` is only the current
    // page, so looking there would find a locker just when it happened to be on
    // screen. The two screens do share the id space — the home summary's
    // `IdPlataforma` is this list's `boxitLocationId` — so the id matches
    // directly; the name is a fallback for links built without one.
    if (
      (!id && !name) ||
      deepLinkResolved.current === location.search ||
      platformSelected ||
      !platformArray?.length
    )
      return;

    const match =
      platformArray.find((p) => id != null && String(p.boxitLocationId) === String(id)) ||
      (name
        ? platformArray.find(
            (p) => (p.locationName ?? '').trim().toLowerCase() === name.trim().toLowerCase()
          )
        : null);

    deepLinkResolved.current = location.search;
    if (match) {
      setPlatformSelected(match);
      // The detail replaces the list in place; landing halfway down the page is
      // the other half of the jump.
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, platformArray]);

  // El botón "Back" y el breadcrumb "lockers" llevan los dos al listado, siempre,
  // sin importar desde dónde se abrió el detalle. Limpiar la selección es lo que
  // de verdad sale del detalle; la query string tiene que irse con ella, o el
  // efecto de deep link volvería a abrir el mismo locker: al refrescar la lista,
  // y otra vez al recargar la página.
  const handleGoToList = () => {
    setPlatformSelected(null);
    if (location.search) history.replace(location.pathname);
    onRefresh();
  };

  // Abrir el detalle desde el listado deja el locker en la URL (el mismo ?locker= del deep link), así
  // recargar la página vuelve a abrirlo en lugar de caer en el listado. Se marca como ya resuelto para
  // que el efecto del deep link no lo procese de nuevo; al recargar se resuelve desde cero.
  const handleOpenDetails = (row) => {
    setPlatformSelected(row);
    const params = new URLSearchParams();
    if (row?.boxitLocationId != null) params.set('locker', row.boxitLocationId);
    else if (row?.locationName) params.set('lockerName', row.locationName);
    const search = params.toString() ? `?${params}` : '';
    deepLinkResolved.current = search;
    if (search) history.replace(`${location.pathname}${search}`);
  };

  const handleExportPlatforms = () => {
    if (!canExportAction) return;
    if (!Array.isArray(filteredPlatforms) || filteredPlatforms.length === 0) return;

    const exportData = filteredPlatforms.map((platform) => {
      const lockerName = platform.locationName ?? platform.Plataforma ?? '-';
      const extId = platform.locationId ?? platform.externalId ?? '-';
      const boxitId = platform.boxitLocationId ?? '-';
      const zipCode = platform.zipCode ?? '-';
      const status = platform.locationStatus === 'ONLINE' ? 'Online' : 'Offline';
      const inactive =
        platform.statusId === 2 || platform.IdEstatus === 2 ? 'Yes' : 'No';

      const typeLockerId = platform.IdTypeLocker ?? platform.typeLocker;
      let lockerType = '-';
      if (typeLockerId === 1) lockerType = 'Indoor';
      else if (typeLockerId === 2) lockerType = 'Outdoor';

      const generation =
        platform.IdGeneration ??
        platform.generation ??
        platform.Generation ??
        null;

      const providerName =
        platform.providerName ?? platform.Fabricante ?? platform.provider ?? null;

      const screenId =
        platform.resolutionScreen ?? platform.screenId ?? platform.ScreenId;
      let screenResolution = '-';
      if (screenId === 1) screenResolution = 'Square';
      else if (screenId === 2) screenResolution = 'Portrait';
      else if (screenId === 5) screenResolution = 'Portrait BR';

      const checkItemRaw = platform.checkItem;
      let checkItem = '-';
      if (checkItemRaw === true || checkItemRaw === 'true') {
        checkItem = 'Enabled';
      } else if (checkItemRaw === false || checkItemRaw === 'false') {
        checkItem = 'Disabled';
      }

      return {
        lockerName,
        externalId: extId,
        boxitId,
        pudoId: platform.pudoId ?? null,
        city: platform.city ?? null,
        address: platform.fisicAddress ?? platform.address ?? null,
        zipCode,
        providerName,
        generation,
        lockerType,
        status,
        inactive,
        screenResolution,
        checkItem,
        turnLampOn: platform.TurnOn ?? null,
        turnLampOff: platform.TurnOff ?? null,
        occupancyPercentage: platform.ocupationLocker ?? 0,
        availableDoors: platform.availableDoors ?? 0,
        totalDoors: platform.totalDoors ?? 0,
        occupiedDoors: platform.occupiedDoors ?? 0,
        reservedDoors: platform.reservedDoors ?? 0,
        availableSmallDoors: platform.availableSmallDoors ?? 0,
        availableMediumDoors: platform.availableMediumDoors ?? 0,
        availableLargeDoors: platform.availableLargeDoors ?? 0,
        availableXLargeDoors: platform.availableXLargeDoors ?? 0,
      };
    });

    const dateLabel = new Date().toISOString().split('T')[0];
    exportToExcelGeneral(`lockers_filtered_${dateLabel}`, 'Lockers', exportData);
  };

  const canExport = canExportAction && !loading && Array.isArray(filteredPlatforms) && filteredPlatforms.length > 0;

  useEffect(() => {
    setLocalSelectedField(selectedField || 'locationName');
  }, [selectedField]);

  useEffect(() => {
    setSelectedStatus(filters.status || 'all');
  }, [filters.status]);

  const fieldOptions = useMemo(
    () => [
      { value: 'locationName', label: translate('dashboard.platforms.details') },
      { value: 'locationId', label: translate('dashboard.platforms.support.openLockerCard.locationId') },
      { value: 'zipCode', label: translate('global.zipCode') },
      { value: 'boxitLocationId', label: translate('global.lockerId') },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      {
        value: 'all',
        label: translate('dashboard.clients.filters.allStatus') || 'Todos los estados',
        icon: <FaIcons.FaList size={16} />,
        showLabel: true,
      },
      {
        value: 'ONLINE',
        label: 'Online',
        icon: <FaIcons.FaSignal size={16} />,
        showLabel: true,
      },
      {
        value: 'OFFLINE',
        label: 'Offline',
        icon: <IoMdAlert size={16} />,
        showLabel: true,
      },
    ],
    []
  );

  const columns = [
    {
      key: 'locationName',
      label: translate('dashboard.platforms.details'),
      sortable: true,
      width: '220px',
      render: (row) => {
        const zip = row.zipCode ? (localStorage.getItem('country') === 'br' ? `CEP: ${row.zipCode}` : `Zip: ${row.zipCode}`) : '';
        const extId = row.locationId ? (zip ? ` / ExtId: ${row.locationId}` : `ExtId: ${row.locationId}`) : '';
        const id = row.boxitLocationId ? ` ID.${row.boxitLocationId}` : '';
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{row.locationName ?? row.Plataforma ?? '-'}</div>
            {(zip || extId || id) && (
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {zip}
                {extId}
                {id}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'ocupationLocker',
      label: translate('global.currentOccupation'),
      sortable: true,
      width: '120px',
      render: (row) => <NumberWithPercentage number={row.ocupationLocker ?? 0} />,
    },
    {
      key: 'locationStatus',
      label: translate('global.status'),
      width: '160px',
      render: (row) => {
        const isOnline = row.locationStatus === 'ONLINE';
        const estatus = row.statusId ?? row.IdEstatus;
        return (
          <div className={styles.statusBadges}>
            <Badge variant={isOnline ? 'success' : 'error'} icon={isOnline ? <FaIcons.FaSignal size={12} /> : <IoMdAlert size={12} />}>
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
            {estatus === 1 && (
              <Badge variant="success" size="sm">
                {translate('global.status.locker.active')}
              </Badge>
            )}
            {estatus === 2 && (
              <Badge variant="archived" size="sm">
                {translate('global.status.locker.inactive')}
              </Badge>
            )}
            {estatus === 3 && (
              <Badge variant="warning" size="sm" icon={<FaIcons.FaTools size={11} />}>
                {translate('global.status.locker.uninstalled')}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'availableDoors',
      label: translate('global.availableDoors'),
      sortable: true,
      width: '100px',
      render: (row) => {
        const available = row.availableDoors ?? 0;
        const total = row.totalDoors ?? 0;
        if (total === 0) return '-';
        return (
          <div className={styles.doorsRatio}>
            <span className={styles.doorsRatioValue} data-variant={available === 0 ? 'empty' : available <= total * 0.2 ? 'low' : 'ok'}>
              {available}/{total}
            </span>
            <div className={styles.doorsRatioBar} role="presentation">
              <div
                className={styles.doorsRatioFill}
                style={{ width: `${total > 0 ? (available / total) * 100 : 0}%` }}
                data-variant={available === 0 ? 'empty' : available <= total * 0.2 ? 'low' : 'ok'}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '120px',
      render: (row) => (
        <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          {canViewDetails && (
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => handleOpenDetails(row)}
              title={translate('global.details')}
              aria-label={translate('global.details')}
            >
              <BiShow size={18} />
            </button>
          )}
          {canEditConfiguration && verifyHideCountryItems(['pa']) && (
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => {
                if (row?.locationId) {
                  window.location.href = `/dashboard/platforms/settings?extId=${row.locationId}&view=edit`;
                }
              }}
              title={translate('global.view') + ' ' + translate('global.configuration')}
              aria-label={translate('global.view') + ' ' + translate('global.configuration')}
            >
              <BiCog size={18} />
            </button>
          )}
          {canSyncAll && (
            <span title={translate('global.sync')}>
              <BtnSync
                id={`btn-sync-${row?.locationId}`}
                mode="force"
                locationId={row?.locationId}
                size="sm"
                variant="btn"
              />
            </span>
          )}
        </div>
      ),
    },
  ];

  if (resolvingDeepLink) {
    return (
      <PageContainer>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner type={1} />
        </div>
      </PageContainer>
    );
  }

  if (platformSelected) {
    return (
      <PlatformDetails
        setPlatformSelected={setPlatformSelected}
        platformSelected={platformSelected}
        onRefresh={onRefresh}
        onBack={handleGoToList}
      />
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={translate('dashboard.platforms.title')}
        subtitle={
          <div className={styles.headerStats}>
            <span>{stats.total.toLocaleString('en-US')} {translate('global.lockers')}</span>
            {stats.online > 0 && (
              <Badge variant="success" size="sm" icon={<FaIcons.FaSignal size={12} />}>
                {stats.online} Online
              </Badge>
            )}
            {stats.offline > 0 && (
              <Badge variant="error" size="sm" icon={<IoMdAlert size={12} />}>
                {stats.offline} Offline
              </Badge>
            )}
          </div>
        }
        icon={<GiIcons.GiPlatform size={28} />}
        actions={
          <div className={styles.headerActions}>
            {canExportAction && (
              <Button
                variant="outline"
                onClick={handleExportPlatforms}
                leftIcon={<TfiExport size={16} />}
                disabled={!canExport}
              >
                {translate('global.export')}
              </Button>
            )}
            {canCreate && verifyHideCountryItems(['pa']) && (
              <Button
                variant="primary"
                onClick={() => setCanViewModalCreate(true)}
                leftIcon={<FaIcons.FaPlus />}
              >
                {translate('global.create')}
              </Button>
            )}
          </div>
        }
      />
      {canSearch && (
        <SearchFilter
          placeholder={translate('global.search') + ' ...'}
          onSearch={handleSearch}
          onFilter={handleFilter}
          activeFilters={filters}
          loading={loading}
          unifiedMode={true}
          selectedField={localSelectedField}
          onFieldChange={setLocalSelectedField}
          fieldOptions={fieldOptions}
          statusFilter={true}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          statusOptions={statusOptions}
        />
      )}

      {error && (
        <ContentCard>
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <strong>{translate('global.error')}:</strong> {error}
          </div>
        </ContentCard>
      )}

      <ExpandableTable
        columns={columns}
        data={platforms}
        loading={loading}
        onSort={handleSort}
        rowClickTogglesExpand={true}
        emptyMessage={translate('dashboard.platforms.notFound')}
        hoverable
        renderExpandedContent={(row) => <PlatformQuickInfo platform={row} />}
        getRowId={(row) => row.boxitLocationId || row.locationId || row.IdPlataforma}
      />

      {!loading && platforms.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {canViewModalCreate && verifyHideCountryItems(['pa']) && (
        <DuplicatePlatformModal
          isView={canViewModalCreate}
          setIsView={setCanViewModalCreate}
          platformArray={platformArray}
        />
      )}
    </PageContainer>
  );
};

export default General;
